import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { canSearchPatientGlobal, canSearchPatientUhid, PATIENT_UHID_SEARCH_LIMIT } from '../../lib/patient-uhid-search';
import {
  archivePatient,
  createPatient,
  fetchPatientCurrentStatus,
  fetchPatientGlobalOptions,
  fetchPatientLookups,
  fetchPatientProfile,
  fetchPatients,
  fetchPatientStats,
  fetchPatientUhidOptions,
  updatePatient,
} from './patients.api';
import type { PatientPayload } from './types';

export const patientKeys = {
  all: ['patients'] as const,
  lists: () => [...patientKeys.all, 'list'] as const,
  list: (page: number, search: string, sortBy: string | null = null, sortOrder: 'asc' | 'desc' = 'asc') =>
    [...patientKeys.lists(), { page, search, sortBy, sortOrder }] as const,
  summary: () => [...patientKeys.all, 'summary'] as const,
  stats: (days: number) => [...patientKeys.all, 'stats', days] as const,
  uhidLookup: (search: string) => [...patientKeys.all, 'uhid-lookup', search] as const,
  globalLookup: (search: string) => [...patientKeys.all, 'global-lookup', search] as const,
  profile: (patientId: string) => [...patientKeys.all, 'profile', patientId] as const,
  currentStatus: (patientId: string) => [...patientKeys.all, 'current-status', patientId] as const,
};

export const usePatients = (page: number, search: string, limit = 10, sortBy: string | null = null, sortOrder: 'asc' | 'desc' = 'asc') =>
  useQuery({
    queryKey: patientKeys.list(page, search, sortBy, sortOrder),
    queryFn: () => fetchPatients({ page, limit, ...(search ? { search } : {}), ...(sortBy ? { sortBy, sortOrder } : {}) }),
  });

export const usePatientUhidLookup = (search: string) =>
  useQuery({
    queryKey: patientKeys.uhidLookup(search),
    queryFn: () => fetchPatientUhidOptions(search, PATIENT_UHID_SEARCH_LIMIT),
    enabled: canSearchPatientUhid(search),
    staleTime: 0,
    refetchOnMount: 'always',
  });

export const usePatientGlobalLookup = (search: string) =>
  useQuery({
    queryKey: patientKeys.globalLookup(search),
    queryFn: () => fetchPatientGlobalOptions(search, PATIENT_UHID_SEARCH_LIMIT),
    enabled: canSearchPatientGlobal(search),
    staleTime: 0,
    refetchOnMount: 'always',
  });

export const usePatientProfile = (patientId: string) =>
  useQuery({
    queryKey: patientKeys.profile(patientId),
    queryFn: () => fetchPatientProfile(patientId),
    enabled: Boolean(patientId),
    staleTime: 0,
    refetchOnMount: 'always',
  });

export const usePatientCurrentStatus = (patientId: string, enabled = true) =>
  useQuery({
    queryKey: patientKeys.currentStatus(patientId),
    queryFn: () => fetchPatientCurrentStatus(patientId),
    enabled: enabled && Boolean(patientId),
    staleTime: 0,
  });

export const usePatientLookup = (type: 'states' | 'districts', extra: { state?: string } = {}) =>
  useQuery({
    queryKey: [...patientKeys.all, 'lookup', type, extra],
    queryFn: () => fetchPatientLookups(type, '', extra),
    enabled: type !== 'districts' || Boolean(extra.state),
    staleTime: 60_000,
  });

export const usePatientsSummary = () =>
  useQuery({
    queryKey: patientKeys.summary(),
    queryFn: () => fetchPatients({ page: 1, limit: 1 }),
    staleTime: 60_000,
  });

export const usePatientStats = (days: 7 | 14 | 30 = 30) =>
  useQuery({
    queryKey: patientKeys.stats(days),
    queryFn: () => fetchPatientStats(days),
    staleTime: 30_000,
  });

export const useCreatePatient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPatient,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: patientKeys.all }),
  });
};

export const useUpdatePatient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ patientId, payload }: { patientId: string; payload: Partial<PatientPayload> }) => updatePatient(patientId, payload),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: patientKeys.all }),
  });
};

export const useArchivePatient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: archivePatient,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: patientKeys.all }),
  });
};
